import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { BaseState, withCrudOperations } from '../crud.state';
import { withGeoDataSelectors } from './geo-data.selectors';
import { GeoDataService } from '../../services/geo-data/geo-data.service';
import {
  KeyValueDistricts,
  GeoData,
  FeatureCollection,
} from '../../models/geo-data';
import { inject } from '@angular/core';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import { downloadBlob } from '../../utility/download';
import {
  filterGeoJsonFeaturesByGroup,
  getDistrictsFromGeoData,
  getDistrictsFromSelection,
  getFeatureCollectionFromSelection,
  getGroupsFromDistricts,
} from './geo-utils';
import { withLogging } from '../features/logger.feature';
import { MenuItem, MenuItemCommandEvent } from 'primeng/api';

export type GeoDataState = BaseState<GeoData>;

export const initialState: GeoDataState = {
  items: {},
  loading: false,
  initialized: false,
  itemsLoaded: false,
  selectedId: '',
  selection: [],
  version: 0,
  lastPersistedId: '',
  trigger: undefined,
  entityType: 'GeoData',
  entityLabel: 'GeoData',
  filter: {
    query: '',
    category: [],
    tags: undefined,
    products: undefined,
    published: null,
    locked: null,
    ids: undefined,
  },
  events: {
    add: ['linkDataLayer'],
    update: ['linkDataLayer'],
  },
};

export const GeoDataStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withCrudOperations<GeoData>(GeoDataService),
  withLogging(initialState.entityType),
  withGeoDataSelectors(),
  withMethods((store, geoDataService = inject(GeoDataService)) => ({
    generate(features: KeyValueDistricts, type: 'Polygon' | 'Point'): GeoData {
      return geoDataService.generateEmptyGeoData(features, type);
    },
    generateByFeatures(
      features: GeoJSON.Feature[],
      currentFeatures?: GeoJSON.Feature[]
    ) {
      return geoDataService.generateByFeatures(features, currentFeatures);
    },
    downloadSelected(filename: string) {
      geoDataService.downloadGeoJson(filename, store.geoJsonFromSelection());
    },
    filterGeoJsonByGroup(
      featureCollection: FeatureCollection,
      groupKey: string
    ) {
      return filterGeoJsonFeaturesByGroup(featureCollection, groupKey);
    },
    getDistrictsById(id: string, filterByGroupKey?: string) {
      return getDistrictsFromGeoData(store.items()[id], filterByGroupKey);
    },
    getGroupsById(id: string) {
      const features = getDistrictsFromGeoData(store.items()[id]);
      return features ? getGroupsFromDistricts(features) : null;
    },
    getDistrictByIdAndKey(id: string, key: string) {
      const districts = this.getDistrictsById(id);
      return districts && districts[key] ? districts[key] : null;
    },
    getFirstDistrictById(id: string) {
      const districts = this.getDistrictsById(id);
      if (!districts) return null;
      const firstDistrict = Object.values(districts)[0];
      return firstDistrict || null;
    },
    hasGroup(id: string, group: string) {
      return !!this.getGroupsById(id)?.includes(group);
    },
    getGeoGroupMenu(
      id: string,
      dataLayerId: string,
      callBack: any
    ): MenuItem | null {
      const groups = this.getGroupsById(id);
      return groups && groups.length > 0
        ? {
            icon: 'fas fa-filter',
            label: 'Gruppe',
            items: groups.map((group: string) => {
              return {
                icon: 'fas fa-circle-nodes',
                label: group,
                command: (event: MenuItemCommandEvent): void => {
                  callBack({ group, dataLayerId });
                },
              };
            }),
          }
        : null;
    },
    convertToXlsx: rxMethod<{ poiRows: Array<string[]>; filename: string }>(
      pipe(
        switchMap(({ poiRows, filename }) => {
          return geoDataService.convertToXlsx(poiRows, filename).pipe(
            tapResponse({
              next: blob => {
                patchState(store, {
                  selection: [],
                });
                downloadBlob(blob, filename);
              },
              error: console.error,
              //finalize: () => patchState(store, { loading: false }),
            })
          );
        })
      )
    ),
  })),
  withComputed(({ selectedItem }) => ({
    getFeatureCollectionFromSelection:
      getFeatureCollectionFromSelection(selectedItem),
    getDistrictsFromSelection: getDistrictsFromSelection(selectedItem),
  })),
  withHooks({
    onInit({ loadAllItemsByPromise }) {
      loadAllItemsByPromise();
    },
    onDestroy() {
      console.log('on destroy');
    },
  })
);
